/* eslint-disable class-methods-use-this */

'use strict';

define('vb/private/pwa/pwaServiceWorkerManagerClass',[
  'ojs/ojconfig',
  'vbc/private/log',
  'vbc/private/trace/spanContext',
  'vbc/private/trace/tracer',
  'vb/private/configLoader',
  'vb/private/constants',
  'vb/private/pwa/swLogOptions',
  'vb/private/pwa/swTraceOptions',
  'vb/private/utils',
  'vbsw/private/serviceWorkerManagerClass',
], (
  ojConfig,
  Log,
  SpanContext,
  Tracer,
  ConfigLoader,
  Constants,
  SwLogOptions,
  SwTraceOptions,
  Utils,
  ServiceWorkerManagerClass,
) => {
  const logger = Log.getLogger('/vb/private/pwa/pwaServiceWorkerManager');

  // Grab a local reference for simpler expressions in the code below.
  // eslint-disable-next-line prefer-destructuring
  const PWA_CONFIG = globalThis.vbInitConfig.PWA_CONFIG;

  /**
   * Check paths and resources to see if they need to have requirejs paths resolved.
   * (Resolve them in place)
   * @param {*} pathInfo
   */
  function resolveRequirePaths(pathInfo) {
    if (pathInfo.path) {
      if (!pathInfo.path.startsWith('http')) {
        // eslint-disable-next-line no-param-reassign
        pathInfo.path = requirejs.toUrl(pathInfo.path);
      }
    } else if (pathInfo.resources) {
      // eslint-disable-next-line no-param-reassign
      pathInfo.resources = pathInfo.resources.map((resource) => {
        if (!resource.startsWith('http')) {
          return requirejs.toUrl(resource);
        }
        return resource;
      });
    }
  }

  /**
   * Check paths and resources in extension digest to see if they need to have requirejs paths resolved.
   * (Resolve them in place)
   * @param {*} extensionDigest
   */
  function resolveExtensionDigestRequirePaths(extensionDigest) {
    if (extensionDigest[Constants.ExtensionDigest.EXTENSION]) {
      extensionDigest[Constants.ExtensionDigest.EXTENSION].forEach((pathInfo) => {
        resolveRequirePaths(pathInfo);
      });
    }
    if (extensionDigest[Constants.ExtensionDigest.LIBRARIES]) {
      Object.values(extensionDigest[Constants.ExtensionDigest.LIBRARIES]).forEach((libraryInfo) => {
        if (libraryInfo.paths) {
          libraryInfo.paths.forEach((pathInfo) => {
            resolveRequirePaths(pathInfo);
          });
        } else {
          resolveRequirePaths(libraryInfo);
        }
      });
    }
    if (extensionDigest[Constants.ExtensionDigest.COMPONENTS]) {
      Object.values(extensionDigest[Constants.ExtensionDigest.COMPONENTS]).forEach((componentInfo) => {
        if (componentInfo.paths) {
          componentInfo.paths.forEach((pathInfo) => {
            resolveRequirePaths(pathInfo);
          });
        } else {
          resolveRequirePaths(componentInfo);
        }
      });
    }
  }

  /**
   * @typedef {Object} ServiceWorkerSetupMessage
   * @property {string} type
   * @property {Object} payload
   */

  /**
   * This callback does any setup work it needs to do, and then optionally returns a setup message to be
   * sent to the service worker.
   * Each callback is responsible for checking any relevant disable flags before doing its work, as these
   * can be changed by an earlier callback.
   *
   * @callback ServiceWorkerSetupFunction
   * @returns {Promise<ServiceWorkerSetupMessage|null>}
   */

  /**
   * @typedef {Object} ServiceWorkerSetupAction
   * @property {string} name
   * @property {ServiceWorkerSetupFunction} setupFunction
   */

  /**
   * @typedef {import('vbsw/private/serviceWorkerManagerClass')
   *   .ExtensionCacheConfigGetter} ExtensionCacheConfigGetter
   */

  /**
   * An implementation of ServiceWorkerManagerClass to support web app PWA's. This type of manager should only be
   * created when globalThis.vbInitConfig.PWA_CONFIG is specified
   *
   * @see PwaUtils.isWebPwaConfig
   */
  class PwaServiceWorkerManagerClass extends ServiceWorkerManagerClass {
    constructor() {
      super();

      // Setup caching flags
      // Note that disableLibraryCaching and disableExtensionCaching may be overridden to true later, but are never
      // overridden to be false.
      // I need to check PWA_CONFIG because serviceWorkerManagerSpec and serviceWorkerManagerFactory don't use
      // globalThis.vbInitConfig.PWA_CONFIG but rather a passed config object. (Seems to be a test-only problem)
      const disableCaching = !PWA_CONFIG || (PWA_CONFIG.disableCaching === true);
      this.config = {
        disableCaching,
        disableLibraryCaching: disableCaching || (PWA_CONFIG.disableLibraryCaching === true),
        disableExtensionCaching: disableCaching || (PWA_CONFIG.disableExtensionCaching === true),
        deferCaching: PWA_CONFIG && PWA_CONFIG.deferCaching,
      };

      // disable workbox logging unless we are in debug mode:
      // https://developers.google.com/web/tools/workbox/guides/configure-workbox#disable_logging
      // eslint-disable-next-line no-underscore-dangle
      globalThis.__WB_DISABLE_DEV_LOGS = true;

      /** @type {Array<ServiceWorkerSetupAction>|null} */
      this.pendingSetupQueue = [];
      this.loadPWAInfoPromise = null;
      this.loadExtensionsDigestPromise = null;
      // Deferred setup can be triggered by App UI loading, so gate it until SW activation configuration finishes.
      this.readyPromise = new Promise((resolve) => {
        this.readyResolver = resolve;
      });
      this.pendingSetupPromise = null;
    }

    isReady() {
      return this.readyPromise;
    }

    getServiceWorkerVersion() {
      return this.wb.messageSW({ type: 'GET_VERSION' })
        .then((version) => {
          logger.info('Version from the service worker:', JSON.stringify(version, null, 2));
          return version;
        })
        .catch((error) => {
          logger.warn('Failed to detect service worker version:', error);
          return undefined;
        });
    }

    /**
     * See https://github.com/w3c/ServiceWorker/issues/799, specifically Jake's example:
     * https://github.com/w3c/ServiceWorker/issues/799#issuecomment-165499718
     * to explain why we can't use navigator.serviceWorker.ready
     * The ready read-only property of the ServiceWorkerContainer interface provides a way of delaying code
     * execution until a service worker is active. It returns a Promise that will never reject, and which waits
     * indefinitely until the ServiceWorkerRegistration associated with the current page has an active worker.
     * Once that condition is met, it resolves with the ServiceWorkerRegistration.
     * Active service worker is not the same thing as the service worker controlling the page
     *
     * MJR ?? Only used in tests/sw/pwaServiceWorkerManagerSpec
     * Test-only function?  Perhaps update pwaServiceWorkerManagerSpec to use something similar what's used in
     * tests/puppeteer/tests/pwaSpecBase.openTestPageWithConfig()
     *
     * @param scriptUrl context root relative service worker script url, for example: sw.js
     * @returns {Promise<void>} a promise that resolves once a service worker with a given scriptUrl is controlling the
     * page
     */
    serviceWorkerReady(scriptUrl) {
      return new Promise((resolve) => {
        const sw = navigator.serviceWorker.controller;
        if (sw && sw.scriptURL.endsWith(scriptUrl)) {
          logger.info('Activated service worker found:', sw.scriptURL);
          resolve(undefined);
        } else {
          this.controllerChangeHandler = (e) => {
            logger.info('navigator.serviceWorker.controller change for', e.target.controller.scriptURL);
            resolve(undefined);
          };
          navigator.serviceWorker.addEventListener('controllerchange', this.controllerChangeHandler);
        }
      });
    }

    /**
     * Install the Service Worker
     * @param modulePath the path from which the service worker should its dependent modules
     * @param externalConfig config object containing external require config, plugins, and logConfig
     * @param Configuration applicationUrl and getBaseUrlFromConfig()
     * @returns {Promise}
     */
    installServiceWorkers(modulePath, externalConfig, Configuration) {
      return this.getServiceWorkerConfig(modulePath, externalConfig, Configuration)
        .then((config) => this.installServiceWorker(config))
        .then((result) => [result])
        .catch((error) => {
          logger.warn('Failed to install service worker:', error);
          return undefined;
        });
    }

    /**
     * Caching is enabled unless vbInitConfig.PWA_CONFIG.disableCaching is set to true.
     * In all other cases, it is enabled.
     */
    isCachingEnabled() {
      return this.config.disableCaching !== true;
    }

    /**
     * Caching is enabled unless all caching is disabled or
     * library caching specifically has been disabled.
     * Library caching may be disabled later:
     *  if the service worker indicates it is not caching
     * @see vbInitConfig.PWA_CONFIG.disableLibraryCaching
     */
    isLibraryCachingEnabled() {
      return this.config.disableLibraryCaching !== true;
    }

    /**
     * Caching is enabled unless all caching is disabled or
     * extension caching specifically has been disabled.
     * Extension caching may be disabled later:
     *  if the digest version is for previewing via the DT, or
     *  if the service worker indicates it is not caching
     * @see vbInitConfig.PWA_CONFIG.disableExtensionCaching
     */
    isExtensionCachingEnabled() {
      return this.config.disableExtensionCaching !== true;
    }

    /**
     * Determine if we should immediately cache resources, or wait until an offline-enabled AppUi is loaded.
     * Host Application (unified) is the type of application that has AppUis.
     * V1 Applications are cached on demand.
     * This can be overridden by an explicit true or false in PWA_CONFIG.deferCaching.
     * @returns {Boolean}
     */
    isDeferredCaching() {
      if (this.config.deferCaching === false) {
        return false;
      }
      return (this.config.deferCaching === true) || Utils.isHostApplication();
    }

    /**
     * Register service worker for PWAs.
     * Despite the name, Service Worker install event won't necessarily happen,
     * because Service Worker might be registered already
     * First, setup the emulatedServiceWorkerWrapper, and then set up the Workbox Service Worker.
     * Importantly, we are NOT waiting for Workbox to complete, because all the inherited apis of the
     * Service Worker Manager only need to have the ServiceWorkerWrapper.
     * @param {object} config SERVICE_WORKER_CONFIG section of vbInitConfig
     * @returns {Promise} ServiceWorkerWrapper
     */
    installServiceWorker(config) {
      this.config = Object.assign(this.config, config);

      // Start the Workbox Setup, but don't wait for it
      Promise.resolve().then(() => {
        if (!('serviceWorker' in navigator)) {
          // Service worker could be unsupported in the browser, or,  explicity disabled.
          // (for example, in private browsing mode on FF, or, when
          // 'Delete cookies when and site data when Firefox is closed' option is set)
          // In these cases, VB application should degrade to use EmulatedServiceWorkerWrapper
          // @ts-expect-error navigator is narrowed after the serviceWorker feature check.
          logger.warn('Service worker is not supported/enabled in the browser:', navigator.userAgent);
          return null;
        }

        const scriptUrlFromConfig = PWA_CONFIG.scriptUrl || 'sw.js';
        this.scriptUrl = `${config.applicationUrl}${scriptUrlFromConfig}`;
        logger.info('Service worker url:', this.scriptUrl);

        // applicationUrl always ends with a trailing slash. This can mean, in some cases, that the service worker
        // is registered in the scope that does not control the initial request. For example, if initial request is:
        // https://fuscdrmsmc280-fa-ext.us.oracle.com/fscmUI/starterapp, applicationUrl will be:
        // https://fuscdrmsmc280-fa-ext.us.oracle.com/fscmUI/starterapp/ and the service worker will be registered in
        // the scope that does not control the initial request. This means that navigator.serviceWorker.controller will
        // be null, and serviceWorkerReady promise will never resolve, resulting in a stuck application.
        // Service worker is not supposed to have a scope broader than its own location
        // (see https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/register)
        // and exceptions require special 'Service-Worker-Allowed' response headers on the service worker script:
        // (see https://developers.google.com/web/ilt/pwa/introduction-to-service-worker#registration_and_scope)
        // Currently, VB does not support this special case and having a mismatch between server configuration and
        // expected service worker scope will result in an error
        // See slack discussion: https://corparch-core-srv.slack.com/archives/G01B7V95FU0/p1603378926081800
        if (!globalThis.location.href.startsWith(config.applicationUrl)) {
          // eslint-disable-next-line max-len
          const scopeError = new Error(`Service worker scope: ${config.applicationUrl} does not match initial request: ${globalThis.location.href}`);
          throw scopeError;
        }
        let sw;

        // When updateViaCache is set to 'imports', sw won't be updated when an imported script changes (on Chrome)
        // This is not necessary, since when imported scripts change (jet, vb, workbox), vbInitConfig injected to sw.js
        // also changes
        // See: https://developers.google.com/web/updates/2019/09/fresher-sw
        const registrationOptions = { updateViaCache: 'imports' };
        return Utils.getResource('workbox-window')
          .then((workbox) => {
            // See https://developers.google.com/web/tools/workbox/modules/workbox-window
            this.wb = new workbox.Workbox(this.scriptUrl, registrationOptions);
            // keep a reference to event handler so it can be removed
            this.workboxEventHandler = this.handleEvent.bind(this);
            this.wb.addEventListener('installed', this.workboxEventHandler);
            this.wb.addEventListener('redundant', this.workboxEventHandler);
            this.wb.addEventListener('activated', this.workboxEventHandler);
            this.wb.addEventListener('waiting', this.workboxEventHandler);
            this.wb.addEventListener('message', this.workboxEventHandler);
            return this.wb.register();
          })
          .catch((error) => {
            // Failed to register service worker. Report error and fall through to install the emulated service worker.
            logger.error('Service worker registration failed with', error);
          })
          .then((r) => {
            if (r) {
              sw = r.installing || r.waiting || r.active;
            }
            if (sw) {
              logger.info('workbox register returned', sw.state, 'service worker');
            }
            if (r && r.active) {
              // Send any pending messages, and other active setup
              this.onActive();
            }
          });
      });

      // Always emulate the service worker
      // Emulated service worker is registered with the scope of what it would be for the old service worker,
      // vbServiceWorker.js, not sw.js. So we can't use config.applicationUrl here, as this will break
      // csrfTokenHandlerPlugin, for one. See https://jira.oraclecorp.com/jira/browse/VBS-11509
      logger.info('emulating service worker functionality with scope:', config.scopes[0]);
      return this.emulateServiceWorker(config.scopes[0]);
    }

    /**
     * Remove all event listeners added to handle this service worker events
     */
    removeEventListeners() {
      if (this.wb && this.workboxEventHandler) {
        this.wb.removeEventListener('installed', this.workboxEventHandler);
        this.wb.removeEventListener('redundant', this.workboxEventHandler);
        this.wb.removeEventListener('activated', this.workboxEventHandler);
        this.wb.removeEventListener('waiting', this.workboxEventHandler);
        this.wb.removeEventListener('message', this.workboxEventHandler);
      }
      if (this.controllerChangeHandler) {
        navigator.serviceWorker.removeEventListener('controllerchange', this.controllerChangeHandler);
      }
    }

    /**
     * Configure the service worker.
     * This sets the log level and may tell the service worker to send trace messages back to this thread.
     * PWA_CONFIG.enableTrace can be used for debugging, since if Tracer.isEnabled is false the service worker
     * will still send messages back to this thread, to be logged to the console.
     * @returns {Promise<Object>}
     */
    configureSW() {
      return Promise.resolve().then(() => {
        if (this.wb) {
          const { logConfig } = this.config;
          // enableTrace can be explicitly turned off or on via PWA_CONFIG (see above for debugging)
          const enableTrace = (PWA_CONFIG.enableTrace !== undefined)
            ? !!PWA_CONFIG.enableTrace
            : Tracer.isEnabled;

          // Get the locale that ConfigLoader has set to ojConfig (or that JET has derived if we haven't set it).
          const locale = ojConfig.getLocale();
          const message = {
            type: 'CONFIGURE_SW',
            payload: {
              logging: {
                level: logConfig && logConfig.level,
                enableTrace,
                enableTraceLog: enableTrace && !!PWA_CONFIG.enableTraceLog,
              },
              i18n: {
                locale,
              },
            },
          };

          // If running Puppeteer, construct a Promise to indicate that the PWA service worker manager has finished
          // its activation.
          if (Utils.isPuppeteerTestMode(globalThis.vbInitConfig)) {
            this.puppeteerReadiness = new Promise((resolve) => {
              // If Trace is enabled, we need to wait until we've received accumulated trace messages.
              // Make a resolver mechanism. this.puppeteerReadinessResolver() will be called when the
              // trace messages are received.
              this.puppeteerReadinessResolver = () => resolve(undefined);
              // Otherwise, we're ready now that we've configured the service worker.
            });
          }

          return this.wb.messageSW(message)
            .then((swConfig) => {
              if (swConfig) {
                // Check if the service worker indicated it isn't caching.
                // This prevents the main thread from loading cache information and sending caching messages
                // that will be ignored.
                if (swConfig.disableExtensionCaching) {
                  this.config.disableExtensionCaching = true;
                }
                if (swConfig.disableLibraryCaching) {
                  this.config.disableLibraryCaching = true;
                }
                if (swConfig.deferCaching === false) {
                  this.config.deferCaching = false;
                }
              }
              return swConfig;
            })
            .finally(() => {
              if (this.puppeteerReadinessResolver && !enableTrace) {
                // If Trace is enabled, we need to wait until we've received accumulated trace messages.
                // Make a resolver mechanism. this.puppeteerReadinessResolver() will be called when the
                // trace messages are received.
                // Otherwise, we're ready now that we've configured the service worker.
                this.puppeteerReadinessResolver();
              }
            });
        }
        return null;
      });
    }

    getApplication() {
      this.applicationPromise = this.applicationPromise || Utils.getResource('vb/private/stateManagement/application');
      return this.applicationPromise;
    }

    informUser(message) {
      logger.warn('Application needs to be reloaded.', message);
      return this.getApplication()
        .then((application) => application.onNewContentAvailable({ message }))
        .catch((error) => {
          logger.error(error);
        });
    }

    /**
     * @typedef TraceMessage
     * @property {string} type ('info' | 'warn' | 'error')
     * @property {number} time the time of the log in millis
     * @property {Object} sw info about the serviceworker
     * @property {string} sw.version
     * @property {string} sw.script_url
     * @property {Array<*>} args parameters passed to the log message
     *
     * @typedef TraceSpan
     * @property {string} type ('span')
     * @property {string} name e.g. ['activate' | 'install' | 'push' | 'notificationclick']
     * @property {number} startTime the start of the span in millis
     * @property {number} endTime the end of the span in millis
     * @property {Object} sw info about the serviceworker
     * @property {string} sw.version
     * @property {string} sw.script_url
     * @property {string} [sw.etc] additional trace context
     * @property {Error} [error] if an error occurred
     * @property {TraceInfo[]} [traceLog] nested TraceMessages
     *
     * @typedef {(TraceSpan & TraceMessage)} TraceInfo
     *
     * @typedef {Object} Trace
     * @method {Object} span
     * @param {Object} spanContext
     * @method finishSpan
     * @param {Object} span
     * @param {Object} spanContext
     */

    /**
     * Send a tracing span to the tracer.
     * @param {Trace} tracer
     * @param {TraceSpan} traceSpan The event trace information from the service worker
     * @param {*} parentSpan Outer Trace span
     */
    tracerSpan(tracer, traceSpan, parentSpan) {
      const traceOptions = new SwTraceOptions(traceSpan, parentSpan);

      const spanContext = new SpanContext().addOperationName(traceOptions.operationName);
      spanContext.addStartSpan(() => (Object.assign({
        msg: traceOptions.getStartMessage(),
        tags: traceOptions.getTags(),
      }, traceOptions.getStartFields())));

      spanContext.addSpanFunction((span) => {
        this.tracerLog(tracer, traceSpan.traceLog || [], span);

        spanContext.addEndSpan(() => (Object.assign({
          msg: traceOptions.getEndMessage(),
        }, traceOptions.getEndFields())));

        const error = traceOptions.getError();
        if (error) {
          spanContext.addError(error);
        }

        tracer.finishSpan(span, spanContext);
      });

      return tracer.span(spanContext);
    }

    /**
     * Send a log message to the tracer.
     * @param {Trace} tracer
     * @param {TraceMessage} traceMessage The log message from the the service worker
     * @param {*} parentSpan Outer Trace span
     */
    tracerMessage(tracer, traceMessage, parentSpan) {
      // Create new span for each log message
      const logOptions = new SwLogOptions(traceMessage, parentSpan);

      const spanContext = new SpanContext().addOperationName(logOptions.operationName);
      spanContext.addStartSpan(() => (Object.assign({
        msg: logOptions.getStartMessage(),
        tags: logOptions.getTags(),
      }, logOptions.getStartFields())));

      spanContext.addSpanFunction((span) => {
        spanContext.addEndSpan(() => (Object.assign({
          msg: logOptions.getEndMessage(),
        }, logOptions.getEndFields())));

        // Do we really need another message for the end span of a simple log message?
        // Or could we just do Tracer.startSpan(spanContext); and leave it at that?
        tracer.finishSpan(span, spanContext);
      });

      tracer.span(spanContext);
    }

    /**
     * Send each entry in the tracelog to the tracer
     *
     * @param {Trace} tracer
     * @param {TraceInfo[]} traceLog
     * @param {*} parentSpan Outer Trace span
     */
    tracerLog(tracer, traceLog, parentSpan = null) {
      traceLog.forEach((traceInfo) => {
        if (traceInfo.type === 'span') {
          this.tracerSpan(tracer, traceInfo, parentSpan);
        } else {
          this.tracerMessage(tracer, traceInfo, parentSpan);
        }
      });
    }

    /**
     * Mimic Tracer api to output to the console
     * @private
     * @returns {Trace}
     */
    static setupConsoleTracer() {
      return {
        span: (spanContext) => {
          const spanOptions = spanContext.startSpanOptions();

          const info = {
            event: spanContext.operationName,
            start_time: new Date(spanOptions.startTime).toISOString(),
          };
          if (spanOptions.tags) {
            Object.assign(info, spanOptions.tags);
          }

          const type = (spanContext.operationName === SwLogOptions.SPAN_NAMES.SW_LOG)
            ? spanOptions.level.toUpperCase() : 'TRACE';
          const messagePrefix = `[VB ${type} ${spanContext.operationName}]`;

          if (spanContext.operationName === SwLogOptions.SPAN_NAMES.SW_LOG) {
            console.log(`${messagePrefix} ${spanOptions.msg}\n`, info);
          } else {
            console.group(`${messagePrefix} ${spanOptions.msg}\n`, info);
          }

          // Call the function with the start span info and message prefix
          spanContext.spanFunction()({ info, messagePrefix });
        },
        finishSpan: (span, spanContext) => {
          const spanOptions = spanContext.endSpanOptions();

          const info = Object.assign({
            end_time: new Date(spanOptions.endTime).toISOString(),
            error: spanOptions.error || false,
          }, span.info);

          if (spanContext.operationName !== SwLogOptions.SPAN_NAMES.SW_LOG) {
            console.groupEnd();
          }
          console.log(`${span.messagePrefix} ${spanOptions.msg}\n`, info);
        },
      };
    }

    reportTraceLog(tracelog) {
      /* @type Trace */
      let tracer;
      // If tracing is enabled, send tracelog to the Tracer
      if (Tracer.isEnabled) {
        // Report TraceLog to the global Tracer
        tracer = {
          span: (spanContext) => Tracer.span(spanContext),
          finishSpan: (span, spanContext) => Tracer.finishSpan(span, spanContext),
        };
      } else {
        // Otherwise, mimic Tracer api to output to the console
        // (This is a debugging / testing only aid.)
        tracer = PwaServiceWorkerManagerClass.setupConsoleTracer();
      }

      this.tracerLog(tracer, tracelog);

      // If running Puppeteer and we've configured service worker for tracing during activation, then
      // indicate we've received the trace dump (and are now ready).
      if (this.puppeteerReadinessResolver) {
        this.puppeteerReadinessResolver();
      }
    }

    handleEvent(event) {
      const state = event.type;
      logger.info('Event handler invoked for service worker \'', state, '\' event');
      switch (state) {
        case 'installed':
          logger.info('Service worker installed', event.isUpdate ? 'after update' : 'for the first time');
          break;
        case 'activated':
        {
          if (event.isUpdate) {
            logger.info('Service worker activated after update');
          }
          if (event.isExternal) {
            this.informUser('New version of the service worker has been activated');
          }

          // Send any pending messages, and other active setup
          this.onActive();
          break;
        }
        case 'redundant':
        {
          logger.info('Service worker is now redundant');
          break;
        }
        case 'waiting':
        {
          logger.info('A new service worker,', event.sw.scriptURL,
            'has installed, but it cannot activate until all tabs running current version have fully unloaded.');
          // debugger;
          logger.warn(event.sw);
          this.wb.messageSkipWaiting();
          break;
        }
        case 'message':
        {
          if ((event.data.message === 'tracelog') && event.data.traceLog) {
            this.reportTraceLog(event.data.traceLog);
          }
          break;
        }
        default:
          logger.warn('Event:', state, 'was not handled');
      }
    }

    /**
     * Run a service worker setup action and send the returned message, if any.
     * @param {ServiceWorkerSetupAction} setupAction
     * @returns {Promise}
     */
    runSetupAction(setupAction) {
      return Promise.resolve()
        .then(() => setupAction.setupFunction())
        .then((message) => {
          if (message) {
            // Report message delivery failures with the action name without stopping subsequent queued setup actions.
            return this.wb.messageSW(message)
              .catch((error) => {
                logger.error(`Failed to send setup action '${setupAction.name}' message`, error);
              });
          }
          return undefined;
        })
        .catch((error) => {
          logger.error(`Failed to execute setup action '${setupAction.name}'`, error);
        });
    }

    /**
     * Run pending setup callbacks after the PWA service worker has activated or, for deferred caching,
     * after an offline-enabled AppUi has loaded.
     * @returns {Promise}
     */
    runPendingSetupActions() {
      // Offline-enabled App UIs can request setup before onActive completes, so wait for readiness first.
      return this.isReady().then(() => {
        if (this.pendingSetupPromise) {
          return this.pendingSetupPromise;
        }

        if (!this.pendingSetupQueue) {
          return undefined;
        }

        const { pendingSetupQueue } = this;
        this.pendingSetupQueue = null;

        this.pendingSetupPromise = pendingSetupQueue.reduce((promise, setupAction) => promise
          .then(() => this.runSetupAction(setupAction)), Promise.resolve());
        return this.pendingSetupPromise;
      });
    }

    onActive() {
      // Set up logging (and tracing)
      this.configureSW()
        // Send message to load and setup the pwa information
        .then(() => this._loadPWAInfo())
        // Send any pending messages (register extensions and libraries)
        .then(() => {
          // Service worker configuration and PWA metadata are ready; queued setup can now be sent safely.
          this.readyResolver(undefined);
          if (!this.isDeferredCaching()) {
            return this.runPendingSetupActions();
          }
          return undefined;
        })
        .finally(() => {
          // If running Puppeteer, then indicate when PWA service worker manager has finished its activation
          if (this.puppeteerReadiness) {
            this.puppeteerReadiness.then(() => {
              delete this.puppeteerReadiness;
              delete this.puppeteerReadinessResolver;

              // send a message to puppeteer tests
              document.dispatchEvent(new Event('vbPwaReady'));
            });
          }
        });
    }

    /* eslint-disable max-len */
    /**
     * Load the library digest from the manifest and evaluate any expressions in it.
     * Send the library digest to the service worker.
     * @see https://confluence.oraclecorp.com/confluence/display/ABCS/Library+caching+specification+for+VB+Progressive+Web+Applications
     * @returns {Promise<ServiceWorkerSetupMessage | null >}
     */
    async _loadLibrariesDigest() {
      if (!this.isLibraryCachingEnabled()) {
        return null;
      }

      let librariesDigest;
      // Embedded directly within PWA_CONFIG?
      if (PWA_CONFIG.libraryManifest) {
        librariesDigest = PWA_CONFIG.libraryManifest;
      } else if (PWA_CONFIG.libraryManifestUrl) {
        // Load the supplied libraryManifest url
        try {
          librariesDigest = await Utils.loadAndParse(PWA_CONFIG.libraryManifestUrl);
        } catch (error) {
          logger.error('PWA Library Digest Load failed', error);
        }
      }

      // Evaluate JS expression and possible usage of $initParams in the library digest
      librariesDigest = ConfigLoader.getEvaluatedSafe(librariesDigest);
      // No digest?  Don't bother sending to the service worker.
      if (!librariesDigest) {
        return null;
      }

      // Translate all requirejs paths to actual paths
      // Each library could have { paths: { path, resources } } or { path, resources }
      Object.values(librariesDigest).forEach((libraryInfo) => {
        if (libraryInfo.paths) {
          libraryInfo.paths.forEach((pathInfo) => {
            resolveRequirePaths(pathInfo);
          });
        } else {
          resolveRequirePaths(libraryInfo);
        }
      });

      // and send it to the service worker.
      return {
        type: 'PROCESS_LIBRARY_DIGEST',
        payload: {
          digest: librariesDigest,
        },
      };
    }

    /**
     * Load the pwa-info from the extension digest and evaluate any expressions in it.
     * eslint-disable max-len
     * @see https://confluence.oraclecorp.com/confluence/display/ABCS/Digest+information+for+caching+VB+PWA+Extensions
     * @returns {Promise}
     */
    _loadExtensionsDigest() {
      this.loadExtensionsDigestPromise = this.loadExtensionsDigestPromise || Promise.resolve()
        .then(() => {
          const { registryConfig } = this.config;

          if (!registryConfig || !registryConfig.digestLoader) {
            this.config.disableExtensionCaching = true;
            return null;
          }

          const { digestLoader } = registryConfig;

          // If the extension version starts with '$', we are in preview mode.
          // Do not cache the digest or extensions.
          const version = digestLoader.getExtensionVersion();
          if (version && version.startsWith('$')) {
            this.config.disableExtensionCaching = true;
            return null;
          }

          if (!this.isExtensionCachingEnabled()) {
            return null;
          }

          // The bootstrap response supplies the exact partial digest URL. Wait for it before collecting
          // cache paths, but retain the normal full-digest cache entries if that request failed.
          const bootstrapPartialDigestPromise = registryConfig.fetchPartialManifestPromise
            ? registryConfig.fetchPartialManifestPromise.catch(() => undefined)
            : Promise.resolve();
          const digestCacheSetupPromise = bootstrapPartialDigestPromise.then(() => {
            const cacheInfo = registryConfig.getDigestPathsForCaching();
            return Promise.all(cacheInfo.map(({ name, path, resources }) =>
              // eslint-disable-next-line implicit-arrow-linebreak
              this._setupLibraryCache(name, version, path, resources)));
          });

          // Load the PWA_INFO digest
          const loadDigestPromise = digestLoader.loadDigests([Constants.Digest.PWA_INFO])[0]
            .catch((error) => {
              logger.error('PWA Extension Digest Load failed', error);
              return null;
            })
            // Evaluate JS expression and possible usage of $initParams in the extension digest
            .then((extensionsDigest) => ConfigLoader.getEvaluatedSafe(extensionsDigest))
            // Replace the extension digest array with a map keyed by extension id.
            .then((extensionsDigest) => {
              if (extensionsDigest && extensionsDigest.length) {
                const extensionDigestMap = {};
                extensionsDigest.forEach((extensionInfo) => {
                  extensionDigestMap[extensionInfo.id] = extensionInfo.metadata;
                  extensionDigestMap[extensionInfo.id].version = extensionInfo.version;
                });
                return extensionDigestMap;
              }
              return null;
            });

          return Promise.all([digestCacheSetupPromise, loadDigestPromise])
            .then(([, extensionsDigest]) => extensionsDigest);
        })
        .finally(() => {
          // registryConfig not needed anymore
          delete this.config.registryConfig;
        });

      return this.loadExtensionsDigestPromise;
    }

    /**
     * Load the library manifest and pwa-info from the extension digest and send it to the service worker to
     * initialize the libraries registry.
     *
     * @returns {Promise}
     */
    _loadPWAInfo() {
      this.loadPWAInfoPromise = this.loadPWAInfoPromise || Promise.resolve()
        .then(() => {
          const setupAction = {
            name: 'load PWA info',
            setupFunction: async () => {
              // Load the libraries digest and the extensions digest in parallel.
              // Don't wait for the extensions digest to load, we wait when we actually want to cache
              // an extension.
              this._loadExtensionsDigest();
              return this._loadLibrariesDigest();
            },
          };

          if (this.pendingSetupQueue) {
            this.pendingSetupQueue.unshift(setupAction);
            return undefined;
          }

          return this.runSetupAction(setupAction);
        });

      return this.loadPWAInfoPromise;
    }

    /**
     * Check component versions.  ExtensionRegistry has ensured that only the latest
     * component version is used, so we need to make sure that is the one that is cached.
     * @param {*} extensionsDigest
     * @param {*} components
     * @returns {*} updated components
     */
    validateComponentVersions(extensionsDigest, components) {
      const validatedComponents = {};
      Object.entries(components).forEach(([name, component]) => {
        const externalComponent = this.externalComponents[name];
        if (externalComponent) {
          // Only process a component once
          delete this.externalComponents[name];

          // If the extension is referencing the current version, use its definition.
          // Otherwise, find the correct definition from another extension so we can
          // cache the current version.
          // In any case, delete all other extension references to the component.
          if (externalComponent.version === component.version) {
            validatedComponents[name] = component;
          } else {
            Object.values(extensionsDigest).forEach((otherExtensionDigest) => {
              const otherExtensionComponent = otherExtensionDigest[Constants.ExtensionDigest.COMPONENTS]
                && otherExtensionDigest[Constants.ExtensionDigest.COMPONENTS][name];
              if (otherExtensionComponent) {
                // eslint-disable-next-line no-param-reassign
                delete otherExtensionDigest[Constants.ExtensionDigest.COMPONENTS][name];
                if (otherExtensionComponent.version === externalComponent.version) {
                  validatedComponents[name] = otherExtensionComponent;
                }
              }
            });
          }
        }
      });

      // Return the validated set of components
      return validatedComponents;
    }

    /**
     * Tell the service worker to initialize an extension's cache.
     * Out of date caches for this extension are deleted.
     * The route for the extension's cache is based on the extension's baseUrl.
     * @param {ExtensionCacheConfigGetter} getExtensionCacheConfig returns the extension cache configuration
     * @returns {Promise}
     */
    setupExtensionCache(getExtensionCacheConfig) {
      const setupAction = {
        name: 'setup extension cache',
        setupFunction: async () => {
          const extensionsDigest = await this._loadExtensionsDigest();

          // _loadExtensionsDigest() may set the disableExtensionCaching flag.
          if (!this.isExtensionCachingEnabled()) {
            return null;
          }

          const {
            id, version, baseUrl, resources,
          } = getExtensionCacheConfig();
          const extensionDigest = extensionsDigest && extensionsDigest[id];
          if (extensionDigest) {
            delete extensionsDigest[id];

            // Remember external components for processing all the extensions
            // This is the master set of external components (latest version of each one referenced).
            if (!this.externalComponents) {
              const { extensionRegistry } = ConfigLoader;
              this.externalComponents = extensionRegistry.externalComponents || {};
              delete extensionRegistry.externalComponents;
            }

            // Check component versions. ExtensionRegistry has ensured that only the latest
            // component version is used, so we need to make sure that is the one that is cached.
            if (extensionDigest[Constants.ExtensionDigest.COMPONENTS]) {
              const validatedComponents = this.validateComponentVersions(extensionsDigest,
                extensionDigest[Constants.ExtensionDigest.COMPONENTS]);

              // Use the validated set of components
              extensionDigest[Constants.ExtensionDigest.COMPONENTS] = validatedComponents;
            }

            // Translate all requirejs paths to actual paths
            resolveExtensionDigestRequirePaths(extensionDigest);
          }

          return {
            type: 'SETUP_EXTENSION_CACHE',
            payload: {
              id,
              version,
              baseUrl,
              resources,
              extensionDigest,
            },
          };
        },
      };

      // If we have a message queue,
      // put the message function on the queue so it can be sent when the service worker is activated
      if (this.pendingSetupQueue) {
        this.pendingSetupQueue.push(setupAction);
        return Promise.resolve();
      }

      return this.runSetupAction(setupAction);
    }

    /**
     * Tell the service worker to initialize a library's cache.
     * Out of date caches for this library are deleted.
     * The route for the library's cache is based on the library's path.
     * @param {string} id
     * @param {string} version
     * @param {string} path
     * @param {Array<string>} resources
     * @returns {Promise}
     */
    _setupLibraryCache(id, version, path, resources) {
      if (this.isLibraryCachingEnabled()) {
        const setupAction = {
          name: 'setup library cache',
          setupFunction: async () => ({
            type: 'SETUP_LIBRARY_CACHE',
            payload: {
              id,
              version,
              path,
              resources,
            },
          }),
        };

        // If we have a message queue,
        // put the message function on the queue so it can be sent when the service worker is activated
        if (this.pendingSetupQueue) {
          this.pendingSetupQueue.push(setupAction);
          return Promise.resolve();
        }

        return this.runSetupAction(setupAction);
      }
      return Promise.resolve();
    }

    /**
     * Determine if the Service worker is in the process of caching extensions.
     * @returns {Promise<string>} 'IDLE' | 'CACHING' | 'WAITING'
     */
    getExtensionCachingStatus() {
      if (!this.loadExtensionsDigestPromise) {
        return Promise.resolve(Constants.ServiceWorkerCachingStatus.WAITING);
      }

      if (this.pendingSetupQueue) {
        // Have some queued messages to send
        return Promise.resolve(this.pendingSetupQueue.length ? Constants.ServiceWorkerCachingStatus.CACHING
          : Constants.ServiceWorkerCachingStatus.WAITING);
      }

      const message = {
        type: 'EXTENSION_CACHING_STATUS',
        payload: {},
      };
      return this.wb.messageSW(message);
    }
  }

  return PwaServiceWorkerManagerClass;
});

